import {
  Component,
  ElementRef,
  HostListener,
  Input,
  computed,
  effect,
  forwardRef,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faCalendar,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { format, isValid, parse, startOfDay, startOfMonth } from 'date-fns';
import {
  NgpDatePicker,
  NgpDatePickerFirstDayOfWeekNumber,
  NgpDatePickerLabel,
  NgpDatePickerNextMonth,
  NgpDatePickerPreviousMonth,
} from 'ng-primitives/date-picker';

import {
  SUI_FORM_FIELD_CONTROL,
  SuiFormFieldControl,
} from '../form-field/form-field-control';
import { SuiInputComponent } from '../input';
import { SuiPopoverDirective, SuiPopoverTriggerDirective } from '../popover';
import { SuiSelectComponent, SuiSelectOptionComponent } from '../select';
import { SuiFormControlBase } from '../shared/cva-helper';
import { resolveSuiBoundary } from '../shared/boundary';
import { SuiCalendarComponent } from './sui-calendar.component';

interface MonthOption {
  value: number;
  label: string;
  disabled: boolean;
}

/**
 * Single-date calendar picker.
 *
 * Wraps the ng-primitives `[ngpDatePicker]`, which owns the full calendar
 * a11y/keyboard contract (grid, roving focus, arrows/Home/End/PageUp-Down,
 * selection, min/max/dateDisabled) and self-provides its state on the inner
 * `[ngpDatePicker]` element — so the shared `<sui-calendar>` child resolves that
 * state through DI with no `hostDirectives`/`provideDatePickerState()` on our
 * side. We add the CVA, the month/year nav header, and `startOfDay`
 * normalisation.
 *
 * **Two presentations:** the default is a **field** — a `sui-input` showing the
 * date-fns-formatted value (parse-on-type) with a calendar-icon trigger opening a
 * `sui-popover` that holds the calendar. `[inline]` opts into rendering the
 * calendar directly (no field/popover). Both share one `[ngpDatePicker]` root and
 * one `calendarBody` template declared *inside* it, so the popover-portaled copy
 * still resolves the primitive's state at its declaration site.
 *
 * Public value type is native `Date | null` (a date-only field — time is
 * stripped via `startOfDay`). The `Date|null ↔ Date|undefined` seam to the
 * primitive lives here. Selection runs through the *controlled* `[date]` input:
 * the primitive emits `dateChange`, we normalise + store, and feed it back —
 * the same controlled pattern the other sui controls use.
 */
@Component({
  selector: 'sui-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FaIconComponent,
    SuiCalendarComponent,
    SuiInputComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiPopoverTriggerDirective,
    SuiPopoverDirective,
    NgpDatePicker,
    NgpDatePickerLabel,
    NgpDatePickerNextMonth,
    NgpDatePickerPreviousMonth,
  ],
  templateUrl: './sui-date-picker.component.html',
  styleUrl: './sui-date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiDatePickerComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiDatePickerComponent),
    },
  ],
})
export class SuiDatePickerComponent
  extends SuiFormControlBase<Date>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-date-picker-${++SuiDatePickerComponent._idCounter}`;
  private _focused = false;
  /** Month to navigate to once the primitive view exists (buffers early writeValue). */
  private _pendingFocusMonth: Date | null = null;

  private readonly _datePicker = viewChild(NgpDatePicker);
  private readonly _popoverTrigger = viewChild(SuiPopoverTriggerDirective);

  /** Mirrors the primitive's focused date (its state is protected) for the label. */
  private readonly _focusedDate = signal<Date>(startOfDay(new Date()));

  /** The field input's text — formatted value, or the user's in-progress typing. */
  readonly inputText = signal<string>('');

  readonly controlType = 'date-picker';

  readonly faPrev = faAngleLeft;
  readonly faNext = faAngleRight;
  readonly faCalendar = faCalendar;
  readonly faClose = faXmark;

  /** Month/year label, e.g. "June 2026" — the grid's accessible name (sr-only). */
  readonly label = computed(() => format(this._focusedDate(), 'MMMM yyyy'));

  /** Selected date pushed into the primitive (`null` → undefined → no selection). */
  readonly primitiveDate = computed(() => this.value() ?? undefined);

  // ── Month/year quick-nav (header selects) ────────────────────────────────
  // Short month names ("Jan", "Feb", …)
  private readonly _monthNames = Array.from({ length: 12 }, (_, m) =>
    format(new Date(2024, m, 1), 'MMM'),
  );

  readonly focusedMonth = computed(() => this._focusedDate().getMonth());
  readonly focusedYear = computed(() => this._focusedDate().getFullYear());

  /** Month options for the focused year — out-of-[min,max] months are disabled. */
  readonly monthOptions = computed<MonthOption[]>(() => {
    const year = this.focusedYear();
    return this._monthNames.map((label, value) => ({
      value,
      label,
      disabled:
        (!!this.min &&
          year === this.min.getFullYear() &&
          value < this.min.getMonth()) ||
        (!!this.max &&
          year === this.max.getFullYear() &&
          value > this.max.getMonth()),
    }));
  });

  /**
   * Selectable years. Bounded by min/max when set; otherwise a ±10 window around
   * today, always widened to include the focused year (so arrow-nav past the
   * window still shows the current year).
   */
  readonly years = computed<number[]>(() => {
    const focused = this.focusedYear();
    const thisYear = new Date().getFullYear();
    let start = this.min
      ? this.min.getFullYear()
      : Math.min(thisYear, focused) - 10;
    let end = this.max
      ? this.max.getFullYear()
      : Math.max(thisYear, focused) + 10;
    start = Math.min(start, focused);
    end = Math.max(end, focused);
    const out: number[] = [];
    for (let y = start; y <= end; y++) {
      out.push(y);
    }
    return out;
  });

  @Input() id = '';
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  /** Predicate disabling individual dates (e.g. weekends). */
  @Input() dateDisabled: (date: Date) => boolean = () => false;
  /** Primitive numbering: 1=Mon … 7=Sun. Defaults to Monday. */
  @Input() firstDayOfWeek: NgpDatePickerFirstDayOfWeekNumber = 1;
  @Input() showWeekNumbers = false;

  /** Render the calendar directly instead of the field + popover. */
  @Input() inline = false;
  /** Placeholder for the field input (field mode only). */
  @Input() placeholder = '';
  /** date-fns format for displaying + parsing the field text. */
  @Input() displayFormat = 'dd MMM yyyy';
  /**
   * Container the calendar popover portals into. Defaults to the nearest
   * `data-sui-popover-boundary` pane (else `body`) - pass this only to override,
   * same seam as select/combobox `[dropdownContainer]`.
   */
  @Input() popoverContainer?: string | HTMLElement;

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  protected get resolvedPopoverContainer(): string | HTMLElement {
    return (
      this.popoverContainer ?? resolveSuiBoundary(this.hostEl.nativeElement)
    );
  }

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  // The primitive's min/max inputs are `Date | undefined`.
  get minDate(): Date | undefined {
    return this.min ?? undefined;
  }
  get maxDate(): Date | undefined {
    return this.max ?? undefined;
  }

  constructor() {
    super();
    // Apply a buffered month-jump once the primitive view is available
    // (writeValue may run before the inner [ngpDatePicker] is queryable).
    effect(() => {
      if (this._datePicker()) {
        this._applyFocusMonth();
      }
    });
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }
  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return !this.value();
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  override writeValue(value: Date | null): void {
    const normalized = value ? startOfDay(value) : null;
    this.value.set(normalized);
    // Never reformat under the user's caret (WAP-1664, see the date-time
    // picker): a valid partial entry commits the value, the parent writes it
    // back, and canonicalising the text here would jump the caret to the end.
    if (!this._fieldFocused) {
      this._syncInputText(normalized);
    }
    // Jump the calendar to the written date's month (external writes only;
    // in-calendar selection goes through onDateChange, which never re-jumps).
    this._pendingFocusMonth = normalized;
    this._applyFocusMonth();
  }

  onDateChange(date: Date | undefined): void {
    const normalized = date ? startOfDay(date) : null;
    this.setValue(normalized);
    this._syncInputText(normalized);
    // Field mode: picking a day commits and closes the popover (close-on-select).
    this._popoverTrigger()?.close();
  }

  /** Close the calendar popover (field mode — the panel's close button). */
  closePopover(): void {
    this._popoverTrigger()?.close();
  }

  /**
   * Field input (WAP-1664): free text with inline validation. While typing the
   * text belongs to the user - it is never rewritten under the caret - and the
   * value is committed only when the text is already canonical (round-trips
   * through the format), so a partial entry such as "2026-09-1 23:59" neither
   * commits an intermediate value nor gets padded to "2026-09-01" mid-word.
   * Anything parseable commits on Enter or blur; unparseable text paints the
   * field invalid and reverts to the committed value on blur.
   */
  onInputText(text: string): void {
    this.inputText.set(text);
    const trimmed = text.trim();
    if (!trimmed) {
      this.inputInvalid.set(false);
      this.setValue(null);
      return;
    }
    const parsed = this._parseInputText(trimmed);
    this.inputInvalid.set(parsed === null);
    if (parsed && format(parsed, this.displayFormat) === trimmed) {
      this._commitParsed(parsed);
    }
  }

  /** Enter commits whatever parses and canonicalises the text in place. */
  onInputEnter(): void {
    this._commitInputText();
  }

  /** True while the field input has focus: the text belongs to the user until blur. */
  private _fieldFocused = false;
  /** The current text does not parse in the display format. */
  readonly inputInvalid = signal(false);

  onInputFocus(): void {
    this._fieldFocused = true;
  }

  onInputBlur(): void {
    this._fieldFocused = false;
    this._commitInputText();
    this.markTouched();
  }

  private _parseInputText(text: string): Date | null {
    const parsed = parse(text, this.displayFormat, new Date());
    return isValid(parsed) ? parsed : null;
  }

  private _commitParsed(parsed: Date): void {
    const normalized = startOfDay(parsed);
    this.setValue(normalized);
    this._datePicker()?.setFocusedDate(
      startOfMonth(normalized),
      'mouse',
      'forward',
    );
  }

  /** Commit a parseable text, then show the committed value canonically (reverts junk). */
  private _commitInputText(): void {
    const parsed = this._parseInputText(this.inputText().trim());
    if (parsed) {
      this._commitParsed(parsed);
    }
    this.inputInvalid.set(false);
    this._syncInputText(this.value());
  }

  /** Mirror the committed value into the field text (skips while actively typing). */
  private _syncInputText(value: Date | null): void {
    this.inputText.set(value ? format(value, this.displayFormat) : '');
  }

  onFocusedDateChange(date: Date): void {
    this._focusedDate.set(date);
  }

  onMonthChange(month: number): void {
    this._jumpTo(this.focusedYear(), month);
  }

  onYearChange(year: number): void {
    this._jumpTo(year, this.focusedMonth());
  }

  /**
   * Jump the calendar to a month/year via the header selects. setFocusedDate
   * clamps to min/max and skips disabled days forward, so an out-of-range pick
   * self-corrects to the nearest valid date.
   */
  private _jumpTo(year: number, month: number): void {
    this._datePicker()?.setFocusedDate(
      startOfMonth(new Date(year, month, 1)),
      'mouse',
      'forward',
    );
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  private _applyFocusMonth(): void {
    const dp = this._datePicker();
    if (dp && this._pendingFocusMonth) {
      dp.setFocusedDate(
        startOfMonth(this._pendingFocusMonth),
        'mouse',
        'forward',
      );
      this._pendingFocusMonth = null;
    }
  }
}
